import { useLocalStorage } from '@mantine/hooks';
import React, {
  createContext,
  useState,
  useEffect,
  useCallback,
  useMemo
} from 'react';
import { useNavigate } from 'react-router-dom';
import {
  getTodayDate,
  parseMongoQueryString,
  formatLocationData
} from '../utils/common';
import ApiClient, { client } from '../utils/api-client';
import { requestForToken } from '../config/firebase.config';

const clientApi = new ApiClient({
  baseUrl: process.env.REACT_APP_API_URL,
  'accept-language': 'en'
});
const AuthContext = createContext();

const convertFullRoleNameIntoShort = (roles) =>
  roles?.map((role) =>
    role
      .split(' ')
      .map((word) => word[0])
      .join('')
  );

export function AuthProvider({ children }) {
  const [role, setRole] = useLocalStorage({ key: 'user-role' });
  const [accessToken, setAccessToken] = useLocalStorage({
    key: 'access-token'
  });
  const [refreshToken, setRefreshToken] = useLocalStorage({
    key: 'refresh-token'
  });
  const [userInfo, setUserInfo] = useLocalStorage({ key: 'user-info' });
  const [date, setDate] = useLocalStorage({ key: 'date' });
  const [exportNumbersCount, setExportNumbersCount] = useLocalStorage({
    key: 'exp_num_cnt'
  });

  const [user, setUser] = useState({});
  const [fcmToken, setFcmToken] = useState('');
  const [systemUser, setSystemUser] = useState(userInfo);
  const [isProfilePicUpdated, setIsProfilePicUpdated] = useState(false);
  const [notificationReceived, setNotificationReceived] = useState(false);
  const [unreadNotificationCount, setUnreadNotificationCount] = useState(0);

  useEffect(() => {
    window.addEventListener('storage', roleChangeListener);
    if (!date || date !== getTodayDate('YYYYMMDD')) {
      setDate(getTodayDate('YYYYMMDD'));
      setExportNumbersCount('01');
    }
    return () => window.removeEventListener('storage', roleChangeListener);
  }, []);

  useEffect(() => {
    const fetchData = () => {
      // Get user info from localStorage with a check for null or malformed data
      const userInfoString = localStorage.getItem('user-info');
      let userInfo = null;
      if (userInfoString) {
        try {
          userInfo = JSON.parse(userInfoString);
        } catch (error) {
          console.error('Error parsing user-info from localStorage:', error);
        }
      }

      const user = userInfo?.data;

      // Get refresh token from localStorage with a check for null or malformed data
      const refreshTokenString = localStorage.getItem('refresh-token');
      let refreshToken = null;
      if (refreshTokenString) {
        try {
          refreshToken = JSON.parse(refreshTokenString);
        } catch (error) {
          console.error(
            'Error parsing refresh-token from localStorage:',
            error
          );
        }
      }

      // Check if user data and refreshToken are valid
      if (!user) {
        console.warn('No user data found in localStorage.');
      }

      if (!refreshToken) {
        console.warn('No refresh token found in localStorage.');
      }

      const updateUserState = () => {
        if (user) {
          setSystemUser(user);
          checkFCMToken(user);
          const userRoles = convertFullRoleNameIntoShort(user?.role);
          setUser({ ...user, __roles: userRoles });
          if (!role && userRoles.length === 1) setRole(userRoles[0]);
        }
      };

      const refreshAccessToken = () => {
        if (refreshToken) {
          client
            .post('/users/refresh', { refreshToken })
            .then((res) => {
              localStorage.setItem(
                'access-token',
                JSON.stringify(res.accessToken)
              );
            })
            .catch((error) => console.error('Error refreshing token:', error));
        }
      };

      const fetchRegions = () => {
        if (!localStorage.getItem('regions')) {
          clientApi
            .get('/locations')
            .then((res) => {
              localStorage.setItem('regions', JSON.stringify(res));
            })
            .catch((error) => console.error('Error fetching regions:', error));
        }
      };

      // Execute all async functions independently
      refreshAccessToken();
      fetchRegions();
      updateUserState();
    };

    fetchData();
  }, [isProfilePicUpdated]);

  useEffect(() => {
    if (systemUser?._id) getUnreadNotificationCount();
    const fetchRegions = () => {
      if (!localStorage.getItem('regions')) {
        clientApi
          .get('/locations')
          .then((res) => {
            localStorage.setItem('regions', JSON.stringify(res));
          })
          .catch((error) => console.error('Error fetching regions:', error));
      }
    };
    fetchRegions();
  }, [notificationReceived, systemUser?._id]);

  const getUnreadNotificationCount = useCallback(async () => {
    try {
      const res = await client.get(
        `/notifications?${parseMongoQueryString(`$filter=receiver_id eq ${systemUser?._id} and is_viewed eq false`, 'receivers', true)}`
      );
      setUnreadNotificationCount(res?.data?.length || 0);
    } catch (err) {
      console.error(err);
    }
  }, [systemUser?._id]);

  const checkFCMToken = useCallback(async (user) => {
    const token = await requestForToken();
    setFcmToken(token);
    if (user?.fcm_token !== token) {
      await client.put(`/system-user?id=${user?._id}`, { fcm_token: token });
    }
  }, []);

  const roleChangeListener = useCallback(
    (e) => {
      if (e.key !== 'user-role') return;
      const userRoles = convertFullRoleNameIntoShort(user?.role);
      if (!userRoles.includes(e.newValue)) logout();
      else setUserRole(e.newValue);
    },
    [user]
  );

  const logout = useCallback(() => {
    localStorage.clear();
    setSystemUser(null);
    setUser({});
  }, []);

  const setUserRole = useCallback(setRole, []);
  const setUserInfoOnLogin = useCallback(
    (info) => {
      const userRoles = convertFullRoleNameIntoShort(info?.data.role);
      setUser({ ...user, __roles: userRoles });
      if (!role && userRoles.length === 1) setRole(userRoles[0]);
      setUserInfo(info);
      setSystemUser(info?.data);
      setAccessToken(info?.accessToken);
      setRefreshToken(info?.refreshToken);
    },
    [role, user]
  );

  const value = useMemo(
    () => ({
      logout,
      user,
      fcmToken,
      systemUser,
      token: accessToken,
      refreshToken,
      role,
      setUserRole,
      setUserInfoOnLogin,
      setNotificationUpdate: () => setNotificationReceived((prev) => !prev),
      notificationReceived,
      unreadNotificationCount,
      setPictureUpdated: () => setIsProfilePicUpdated((prev) => !prev),
      isProfilePicUpdated
    }),
    [
      logout,
      role,
      fcmToken,
      systemUser,
      user,
      accessToken,
      refreshToken,
      unreadNotificationCount,
      notificationReceived,
      isProfilePicUpdated
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = React.useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within an AuthProvider');
  return context;
}
